import React from 'react';
import { Box, Card, IconButton, Menu, Skeleton } from '@mui/material';
import { MoreVert as MoreVertIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material';
import {
  ContentTitle,
  ContentDescription,
  ContentSearchSnippet,
  ContentStatusPill,
  ContentStats,
  ContentProcessingBadge,
  DocumentIllustration,
  FileTypePill,
  type IllustrationFileType,
} from './atoms';
import { useContentThumbnail } from '../../hooks/useContentThumbnail';

type Content = Parameters<typeof ContentStatusPill>[0]['content'] &
  Parameters<typeof ContentStats>[0]['content'] &
  Parameters<typeof ContentTitle>[0]['content'] &
  Parameters<typeof ContentDescription>[0]['content'] &
  Parameters<typeof ContentSearchSnippet>[0]['content'] &
  Parameters<typeof ContentProcessingBadge>[0]['content'] & {
    id?: string;
    thumbnailUrl?: string;
    thumbnail_url?: string;
    mimeType?: string;
    mime_type?: string;
    fileName?: string;
    file_name?: string;
  };

export interface ContentRowProps {
  content: Content;
  show?: {
    thumbnail?: boolean;
    description?: boolean;
    meta?: boolean;
    stats?: boolean;
    actions?: boolean;
  };
  onClick?: (content: Content) => void;
  loading?: boolean;
  fileType?: IllustrationFileType;
  thumbnailUrl?: string;
  /**
   * Action icons (plain `IconButton` nodes) for the right-side vertical
   * action column. Stacked above the kebab. Bookmark slot is rendered
   * separately above these.
   */
  actions?: React.ReactNode[];
  kebabActions?: React.ReactNode[];
  bookmark?: React.ReactNode;
  selected?: boolean;
  sx?: SxProps<Theme>;
}

const resolveType = (
  content: Pick<Content, 'mimeType' | 'mime_type' | 'fileName' | 'file_name'>,
): IllustrationFileType => {
  const mimeType = (content.mimeType ?? content.mime_type ?? '').toLowerCase();
  const name = (content.fileName ?? content.file_name ?? '').toLowerCase();
  if (mimeType.startsWith('audio/') || /\.(wav|mp3|flac|aac|ogg|m4a)$/.test(name)) return 'audio';
  if (mimeType.startsWith('video/') || /\.(mp4|mov|webm|avi|mkv)$/.test(name)) return 'video';
  if (mimeType.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/.test(name)) return 'img';
  if (mimeType === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint') || /\.pptx?$/.test(name)) return 'ppt';
  if (mimeType === 'text/html' || /\.html?$/.test(name)) return 'html';
  return 'doc';
};

/**
 * List-shaped content row. Three-column layout:
 *
 *   [ tinted illustration strip ] [ body + side metadata ] [ action column ]
 *
 * Body groups stay stacked (title / description / pill cluster) and the
 * side-metadata column (rating, view/download counts, file size) collapses
 * to `display: none` on narrow viewports. Action column is always shown.
 *
 * No auth, no router — caller provides `onClick`, `actions`, `kebabActions`,
 * and `bookmark`.
 */
export const ContentRow: React.FC<ContentRowProps> = ({
  content,
  show,
  onClick,
  loading,
  fileType,
  thumbnailUrl,
  actions,
  kebabActions,
  bookmark,
  selected,
  sx,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const closeMenu = () => setMenuAnchorEl(null);
  const showThumbnail = show?.thumbnail !== false;
  const showDescription = show?.description !== false;
  const showMeta = show?.meta !== false;
  const showStats = show?.stats !== false;
  const showActions = show?.actions !== false && Array.isArray(actions) && actions.length > 0;
  const kebabPresent = Array.isArray(kebabActions) && kebabActions.length > 0;
  const resolvedType = fileType ?? resolveType(content);
  const thumbnailHint =
    thumbnailUrl ?? content.thumbnailUrl ?? content.thumbnail_url;
  const resolvedThumbnailUrl = useContentThumbnail(content.id, thumbnailHint);

  if (loading) {
    return (
      <Card
        sx={[
          { display: 'flex', alignItems: 'stretch', minHeight: 96, mb: 1 },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <Skeleton variant="rectangular" width={160} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, p: 2 }}>
          <Skeleton variant="text" height={20} />
          <Skeleton variant="text" height={16} sx={{ mt: 0.5, width: '70%' }} />
          <Skeleton variant="text" height={14} sx={{ mt: 0.5, width: '40%' }} />
        </Box>
      </Card>
    );
  }

  return (
    <Card
      onClick={onClick ? () => onClick(content) : undefined}
      sx={[
        (theme) => ({
          display: 'flex',
          alignItems: 'stretch',
          minHeight: 96,
          mb: 1,
          position: 'relative',
          cursor: onClick ? 'pointer' : 'default',
          overflow: 'hidden',
          transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)',
          },
          ...(selected && {
            borderColor: theme.palette.brand.cyan,
            boxShadow: `0 0 0 2px ${theme.palette.brand.cyan}`,
          }),
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {/* Column 1: thumbnail image when available, otherwise tinted illustration strip */}
      {showThumbnail && (
        <Box
          sx={(theme) => ({
            width: 160,
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: resolvedThumbnailUrl
              ? undefined
              : theme.palette.fileTints?.[resolvedType] ?? theme.palette.action.hover,
          })}
        >
          {resolvedThumbnailUrl ? (
            <Box
              component="img"
              src={resolvedThumbnailUrl}
              alt={content.title ?? ''}
              loading="lazy"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top',
              }}
            />
          ) : (
            <DocumentIllustration fileType={resolvedType} size="compact" />
          )}
        </Box>
      )}

      {/* Column 2: body — flex 1, contains text content and (on wide screens) side metadata */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          py: 1.5,
          px: 2,
        }}
      >
        {/* Body left: text content */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <ContentTitle content={content} clamp={1} sx={{ minWidth: 0 }} />
          <ContentSearchSnippet content={content} />
          {showDescription && (
            <ContentDescription
              content={content}
              clamp={1}
              sx={{ display: { xs: 'none', sm: '-webkit-box' } }}
            />
          )}
          {showMeta && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
              <FileTypePill fileType={resolvedType} variant="inline" />
              <ContentStatusPill content={content} />
              <ContentProcessingBadge content={content} />
            </Box>
          )}
        </Box>

        {/* Body right: side metadata stack — hides on narrow viewports */}
        {showStats && (
          <ContentStats
            content={content}
            direction="column"
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexShrink: 0,
              minWidth: 110,
            }}
          />
        )}
      </Box>

      {/* Column 3: vertical action column — bookmark, action icons, kebab */}
      {(bookmark || showActions || kebabPresent) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            px: 1,
            py: 1,
            flexShrink: 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {bookmark}
          {showActions &&
            actions!.map((action, i) => (
              <React.Fragment key={i}>{action}</React.Fragment>
            ))}
          {kebabPresent && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchorEl(e.currentTarget);
              }}
              sx={(theme) => ({ color: theme.palette.text.secondary })}
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
      )}

      {selected && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 3,
            color: theme.palette.brand.cyan,
            background: theme.palette.background.paper,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
          })}
        >
          <CheckCircleIcon sx={{ fontSize: 18 }} />
        </Box>
      )}

      {kebabPresent && (
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={closeMenu}
          onClick={(e) => e.stopPropagation()}
        >
          {kebabActions!.map((action, i) => (
            <React.Fragment key={i}>{action}</React.Fragment>
          ))}
        </Menu>
      )}
    </Card>
  );
};

export default ContentRow;
